"use client";

import { createStyleSpec } from "@blocknote/core";

/**
 * 인라인 글자 크기 커스텀 스타일.
 *
 * - propSchema "string": 값으로 "18px" 같은 CSS 크기를 가진다.
 * - HTML 직렬화: `<span data-style-type="fontSize" data-value="18px" style="font-size:18px">`
 *   (BlockNote가 data-* 속성과 파싱 규칙을 자동 생성 → 신버전 에디터 간 복사/붙여넣기 왕복 보장,
 *   구버전 에디터는 매칭 파싱 규칙이 없어 조용히 무시)
 *
 * ⚠️ 저장 JSON 하위호환: BlockNote는 styles 맵에 스키마에 없는 키가 있으면
 * `style ... not found in styleSchema` 예외를 던지므로, 저장 JSON에는
 * styles.fontSize 대신 형제 키 fontSize로 직렬화한다.
 * → src/utils/font-size-serialization.ts (liftFontSize/lowerFontSize) 참고.
 */
export const FontSize = createStyleSpec(
  {
    type: "fontSize",
    propSchema: "string",
  },
  {
    // 바닐라(동기) 스타일 스펙: DOM을 직접 반환한다.
    // createReactStyleSpec는 span 내용이 비동기 렌더되어, 적용 직후 동기 시점에
    // 선택 영역의 DOM 좌표가 (0,0)으로 잡혀 BlockNote 포매팅 툴바가 좌상단으로
    // 튀는 문제가 있었다(굵게/기울임 등 네이티브 마크는 동기 렌더라 정상).
    render: (value) => {
      const span = document.createElement("span");
      span.style.fontSize = value;
      return { dom: span, contentDOM: span };
    },
  },
);

/** 툴바 드롭다운에서 제공하는 프리셋 (본문 기본은 14px = "기본") */
export const FONT_SIZE_PRESETS = [
  "10px",
  "12px",
  "14px",
  "16px",
  "18px",
  "20px",
  "24px",
  "28px",
] as const;

export type FontSizePreset = (typeof FONT_SIZE_PRESETS)[number];

/** 글자 크기 1px 스테퍼의 허용 범위/기본값 (사용자 결정: 8~96px). */
export const FONT_SIZE_MIN = 8;
export const FONT_SIZE_MAX = 96;
/** "기본"(명시 fontSize 없음)일 때 기준이 되는 본문 px. */
export const FONT_SIZE_DEFAULT_PX = 14;
export const FONT_SIZE_STEP = 1;

/** "18px" → 18, "" / 파싱 실패 → 14(기본). */
export function parseFontSizePx(size: string): number {
  const n = parseInt(size, 10);
  return Number.isFinite(n) ? n : FONT_SIZE_DEFAULT_PX;
}

/** 8~96 범위로 보정 + 정수 반올림. */
export function clampFontSizePx(px: number): number {
  return Math.min(FONT_SIZE_MAX, Math.max(FONT_SIZE_MIN, Math.round(px)));
}

/** 숫자 → "Npx" (clamp 포함). */
export function toFontSizeValue(px: number): string {
  return `${clampFontSizePx(px)}px`;
}

/**
 * 현재 선택 영역의 글자 크기를 읽는다.
 *
 * BlockNote의 `getActiveStyles()`는 `selection.$to.marks()`(선택 끝 위치의 마크)만
 * 보기 때문에, **여러 블록(문단/리스트)에 걸친 선택**에서는 fontSize가 균일하게
 * 적용돼 있어도 `$to`가 블록 경계에 놓여 빈 값("")을 돌려준다. 그 결과 스테퍼의
 * 기준값이 기본(14)에 고정돼 +/− 연속 클릭이 누적되지 않는 버그가 있었다.
 *
 * 여기서는 선택 범위 전체의 텍스트 노드를 스캔해 fontSize가 균일하면 그 값을,
 * 섞여 있거나 일부만 적용돼 있으면 ""(기본)을 돌려준다. (collapsed 커서는
 * 기존처럼 커서 위치의 마크를 읽는다.)
 */
export function readSelectionFontSize(editor: unknown): string {
  const ed = editor as {
    _tiptapEditor?: any;
    getActiveStyles?: () => Record<string, string>;
  };
  // tiptap state에 접근할 수 없으면(예: 테스트 mock) getActiveStyles로 대체.
  const fallback = () => {
    try {
      return ed?.getActiveStyles?.().fontSize || "";
    } catch {
      return "";
    }
  };
  try {
    const tt = ed._tiptapEditor;
    const state = tt?.state;
    if (!state) return fallback();
    const sel = state.selection;

    // collapsed 커서: 저장 마크 또는 커서 위치의 마크
    if (sel.empty) {
      const marks = state.storedMarks || sel.$to.marks();
      const m = marks?.find?.((mk: any) => mk.type?.name === "fontSize");
      return m?.attrs?.stringValue || "";
    }

    // 범위 선택: [from, to] 안의 모든 텍스트 노드를 스캔
    let value: string | null = null; // null=아직 미관측
    let mixed = false;
    state.doc.nodesBetween(sel.from, sel.to, (node: any) => {
      if (mixed || !node.isText) return !mixed;
      const m = node.marks?.find?.((mk: any) => mk.type?.name === "fontSize");
      const v: string = m?.attrs?.stringValue || "";
      if (value === null) value = v;
      else if (value !== v) mixed = true;
      return !mixed;
    });
    return mixed ? "" : value || "";
  } catch {
    return fallback();
  }
}
